import React, { useEffect, useState } from 'react';
import Accordion from '@src/components/reusable/Accordion';
import SummaryDrawer from './SummaryDrawer';
import { useAppDispatch, useAppSelector, useTranslate, useWidgetParam } from '@src/core/hooks';
import AuthForm from './AuthForm';
import { authSelector, buyerIsForeign, buyerNeedsPhoneVerificationSelector } from '@src/features/auth';
import UserContactInfo from './UserContactInfo';
import { MdCheckCircle } from 'react-icons/md';
import PaymentStep from './PaymentStep';
import { directLinkStateSelector, setupDirectLinkCheckout } from '@src/features/directLink';
import { currentSaleSelector } from '@src/features/sale';
import { confirmCartOrder, currentOrderSelector } from '@src/features/order';
import Hidden from '@src/components/ui2/Hidden';
import Summary from './Summary';

import {
  Container,
  Summary as SummaryStep,
  Steps,
  StepNumber,
  Step,
  StepHeader,
  StepTitle,
  StepContainer,
} from './styles';
import { SaleState, OrderStatus } from '@src/core/buyerApiClient';
import { breakpoints } from '@src/common/breakpoints';
import useMediaQuery from '@src/hooks/useMediaQuery';

type CheckoutProps = {
  onSuccess?: () => void;
};

const Checkout = (props: CheckoutProps) => {
  const { onSuccess = () => {} } = props;

  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();

  const { directLink, status } = useAppSelector(directLinkStateSelector);
  const currentSale = useAppSelector(currentSaleSelector);
  const currentOrder = useAppSelector(currentOrderSelector);
  const { logged, buyer, buyerToken } = useAppSelector(authSelector);
  const fillCustomerForm = useWidgetParam('fill-customer-form');
  const needsPhoneVerification = useAppSelector(buyerNeedsPhoneVerificationSelector);

  const isMobile = useMediaQuery(breakpoints.mobile);

  const [authOpen, setAuthOpen] = useState<boolean>(true);
  const [paymentOpen, setPaymentOpen] = useState<boolean>(false);
  const [summaryOpen, setSummaryOpen] = useState<boolean>(!isMobile);
  const [authFinished, setAuthFinished] = useState<boolean>(false);

  const AuthStepHeader = (_: boolean, title: string) => {
    return (
      <StepHeader>
        <StepTitle $finished={authFinished}>{title}</StepTitle>

        {authFinished ? (
          <MdCheckCircle size={38} color='#0E8B51' />
        ) : (
          <StepNumber $finished={authFinished}>1</StepNumber>
        )}
      </StepHeader>
    );
  };

  const PaymentStepHeader = (_: boolean, title: string) => {
    const isFinished =
      authFinished &&
      (currentSale?.state === SaleState.PaymentComplete || currentOrder?.status === OrderStatus.Confirmed);

    return (
      <StepHeader>
        <StepTitle $finished={isFinished}>{title}</StepTitle>

        {isFinished ? <MdCheckCircle size={38} color='#0E8B51' /> : <StepNumber $finished={isFinished}>2</StepNumber>}
      </StepHeader>
    );
  };

  useEffect(() => {
    if (logged && buyer?.termsAgreementAt && !buyer?.phoneVerifiedAt == !needsPhoneVerification) {
      setAuthFinished(true);

      if (isMobile) {
        setAuthOpen(false);
      }
    } else {
      setAuthFinished(false);
    }
  }, [logged, buyer, buyerToken]);

  useEffect(() => {
    if (authFinished && status === 'loaded') {
      if (directLink?.type === 'cart') {
        dispatch(confirmCartOrder());
      }

      setPaymentOpen(true);
    }
  }, [authFinished, status]);

  useEffect(() => {
    if (directLink?.type === 'sale' && directLink.saleId) {
      dispatch(setupDirectLinkCheckout(directLink.saleId));
    }
  }, [logged, buyerToken]);

  return (
    <div>
      <Container>
        <Steps>
          <Step>
            <Accordion
              open={authOpen}
              onClick={() => setAuthOpen(!authOpen)}
              title={i18n('direct_link_auth_step_title')}
              header={AuthStepHeader}
            >
              <StepContainer>
                {!logged ||
                !buyer?.termsAgreementAt ||
                (logged && !buyer?.phoneVerifiedAt && needsPhoneVerification) ? (
                  <AuthForm fillCustomerForm={fillCustomerForm === 'true'} editUser={!buyer?.termsAgreementAt} />
                ) : (
                  <UserContactInfo />
                )}
              </StepContainer>
            </Accordion>
          </Step>

          <Step>
            <Accordion
              open={paymentOpen}
              disabled={!authFinished}
              onClick={() => setPaymentOpen(!paymentOpen)}
              titleIcon={() => <StepNumber>2</StepNumber>}
              title={i18n('direct_link_payment_step_title')}
              header={PaymentStepHeader}
            >
              {currentSale && authFinished ? (
                <StepContainer>
                  <PaymentStep sale={currentSale} directLink={directLink} order={currentOrder} onSuccess={onSuccess} />
                </StepContainer>
              ) : null}
            </Accordion>
          </Step>
        </Steps>

        <SummaryStep>
          <Accordion
            open={summaryOpen}
            onClick={() => setSummaryOpen(!summaryOpen)}
            titleIcon={() => null}
            title={i18n('direct_link_summary_title_sale')}
          >
            <StepContainer>
              <Summary directLink={directLink!} showTotal />
            </StepContainer>
          </Accordion>
        </SummaryStep>

        <Hidden smUp>
          <SummaryDrawer open={summaryOpen} onToggle={() => setSummaryOpen(!summaryOpen)} />
        </Hidden>
      </Container>
    </div>
  );
};

export default Checkout;
